import { ToastrService } from 'ngx-toastr';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ValidationErrors } from '@angular/forms';

import { ISnListResponse, ISnListQueryParameters } from '@lib/api';
import { SnCallSign } from '@lib/api/entities';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnCallSignsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _dialogService: SnDialogService,
    private readonly _toastrService: ToastrService,
  ) {}

  public getCallSignList(params?: ISnListQueryParameters): Observable<ISnListResponse<SnCallSign>> {
    return this._httpClient.get('/api/call-signs/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((callSign) => {
          return new SnCallSign(callSign);
        }),
      })),
    );
  }

  public bulkCreateCallSigns(teamId: string, callSigns: SnCallSign[]): Observable<SnCallSign[]> {
    return this._httpClient.post(`/api/teams/${teamId}/call-signs/`, callSigns).pipe(
      map((response: any) =>
        response.map((callSign) => {
          return new SnCallSign(callSign);
        }),
      ),
    );
  }

  public bulkUpdateCallSigns(teamId: string, callSigns: SnCallSign[]): Observable<SnCallSign[]> {
    return this._httpClient.put(`/api/teams/${teamId}/call-signs/`, callSigns).pipe(
      map((response: any) =>
        response.map((callSign) => {
          return new SnCallSign(callSign);
        }),
      ),
    );
  }

  public validateUniqueName(name: string): Observable<ValidationErrors> {
    if (!name) {
      return of(null);
    }

    return this.getCallSignList({ name }).pipe(
      map(({ count }) => (count ? { unique: true } : null)),
    );
  }

  public removeCallSign(id: string): Observable<any> {
    return this._dialogService
      .confirm({
        message: 'Are you sure you want to remove this call sign?',
        confirmButton: {
          text: 'DELETE',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.post(`/api/users/${id}/remove-call-sign/`, {})),
        tap({
          next: () => this._toastrService.success('Call sign removed from user'),
          error: () => this._toastrService.error('Unable to remove call sign from user'),
        }),
      );
  }
}
